import { Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { renderUIComponent } from '../../components/chat-messages/renderUIComponent';
import type { RenderedBoxChildrenInner } from '../../gen/openapi';
import { RenderedBoxTypeEnum } from '../../gen/openapi';
import { useWidgetFocusNonce } from '../WidgetFocusProvider';

// The generated tree type is the generator's flattened `RenderedBox | UiComponent`, so `type`
// is a plain string rather than a discriminant — hence an explicit check.
const isBox = (node: RenderedBoxChildrenInner): boolean => node.type === RenderedBoxTypeEnum.Box;

// One-shot glow: the outer halo expands and softens, landing exactly on the steady ring below it.
const widgetFocusPulse = keyframes`
  0% {
    box-shadow: 0 0 0 2px var(--focus-ring), 0 0 0 10px color-mix(in srgb, var(--focus-ring) 45%, transparent);
  }
  100% {
    box-shadow: 0 0 0 2px var(--focus-ring), 0 0 0 6px color-mix(in srgb, var(--focus-ring) 20%, transparent);
  }
`;

const WidgetAnchor = styled.div<{ $focused: boolean }>`
  position: relative;
  min-width: 0;
  border-radius: 3px;
  max-width: 100%;
  /* Smoothly fade the halo out when the highlight goes away. */
  transition: box-shadow 0.45s ease;

  ${({ $focused }) =>
    $focused &&
    css`
      --focus-ring: ${({ theme }) => theme.infoPrimary};
      box-shadow: 0 0 0 2px var(--focus-ring), 0 0 0 6px color-mix(in srgb, var(--focus-ring) 20%, transparent);
    `}
`;

/**
 * The pulse lives on its own empty overlay, keyed by focus nonce, because that is what makes a
 * repeat click flash again: a one-shot animation only plays when it is *applied*, and re-rendering
 * with an unchanged `animation-name` replays nothing. Remounting the element re-applies it — and an
 * empty absolutely-positioned div is free to remount, where keying the widget itself would tear
 * down its charts. The steady ring stays on the anchor above, so it survives the overlay.
 */
const FocusPulse = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 3px;
  pointer-events: none;
  animation: ${widgetFocusPulse} 0.9s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Nearest ancestor that actually scrolls, or null (so scrolling stays inside the panel). */
const getScrollableParent = (el: HTMLElement): HTMLElement | null => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
};

/**
 * Center a widget within its own scroll container — not via `scrollIntoView`, which scrolls
 * every scrollable ancestor (up to the document) and shifts the whole layout.
 */
const scrollWidgetIntoView = (el: HTMLElement): void => {
  const container = getScrollableParent(el);
  if (!container) return;
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const delta = elRect.top - containerRect.top - (container.clientHeight - elRect.height) / 2;
  container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
};

/**
 * Leaf wrapper: owns the widget's focus behavior. Being focused is React state
 * (`WidgetFocusProvider`), not a class poked into the DOM, so a re-render can't wipe the
 * highlight — and the widget scrolls itself into view rather than being queried for.
 */
const WidgetLeaf: React.FC<{ node: RenderedBoxChildrenInner }> = ({ node }) => {
  // A nonce rather than a boolean, so a repeat click on an already-focused widget scrolls again.
  const focusNonce = useWidgetFocusNonce(node.id);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusNonce !== null && ref.current) scrollWidgetIntoView(ref.current);
  }, [focusNonce]);

  return (
    <WidgetAnchor ref={ref} $focused={focusNonce !== null} data-widget-id={node.id ?? undefined}>
      {focusNonce !== null && <FocusPulse key={focusNonce} data-test-widget-focus-pulse />}
      {renderUIComponent(node.type, node.content)}
    </WidgetAnchor>
  );
};

/**
 * Recursively renders a dashboard layout tree: `Box` nodes nest, leaves go to the registry.
 *
 * A title gets its own column wrapper rather than joining the box's children: a titled row would
 * otherwise lay its heading out as the first cell, beside the widgets instead of above them.
 */
export const LayoutNode: React.FC<{ node: RenderedBoxChildrenInner }> = ({ node }) => {
  if (isBox(node)) {
    const layout = (
      <Box display="flex" flexDirection="column" gap="16px" minWidth={0} style={node.cssProperties}>
        {(node.children ?? []).map((child, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static server-derived tree, stable order
          <LayoutNode key={index} node={child} />
        ))}
      </Box>
    );
    if (!node.title) return layout;
    return (
      <Box display="flex" flexDirection="column" gap="16px" minWidth={0}>
        <Text variant="Large" color="textPrimary">
          {node.title}
        </Text>
        {layout}
      </Box>
    );
  }
  return <WidgetLeaf node={node} />;
};
